/**
 * Safari-safe fetch wrapper
 * Uses a Promise-based XMLHttpRequest approach as primary method for POST
 * to completely avoid Safari's fetch body stream issues
 */

// Detect if we're on Safari/iOS
const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
};

/**
 * XMLHttpRequest-based POST that avoids Safari fetch issues entirely
 */
function xhrPost(url, bodyObject, headers = {}) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    
    // Set headers
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    
    xhr.onload = function() {
      let data;
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        data = { text: xhr.responseText, parseError: true };
      }
      
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        data,
        parseError: null,
        headers: null,
        networkError: false
      });
    };
    
    xhr.onerror = function() {
      resolve({
        ok: false,
        status: 0,
        data: { error: 'Network error', detail: 'Could not connect to server' },
        parseError: new Error('Network error'),
        headers: null,
        networkError: true
      });
    };
    
    xhr.ontimeout = function() {
      resolve({
        ok: false,
        status: 0,
        data: { error: 'Timeout', detail: 'Request timed out' },
        parseError: new Error('Timeout'),
        headers: null,
        networkError: true
      });
    };
    
    xhr.timeout = 30000; // 30 second timeout
    
    try {
      xhr.send(JSON.stringify(bodyObject));
    } catch (e) {
      resolve({
        ok: false,
        status: 0,
        data: { error: 'Request failed', detail: e.message },
        parseError: e,
        headers: null,
        networkError: true
      });
    }
  });
}

/**
 * Standard fetch-based approach for non-Safari browsers
 */
async function fetchPost(url, bodyObject, headers = {}) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...headers
      },
      body: JSON.stringify(bodyObject),
      cache: 'no-store'
    });
    
    let data;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = { parseError: true };
    }
    
    return {
      ok: response.ok,
      status: response.status,
      data,
      parseError: null,
      headers: response.headers,
      networkError: false
    };
  } catch (e) {
    return {
      ok: false,
      status: 0,
      data: { error: 'Network error', detail: e.message },
      parseError: e,
      headers: null,
      networkError: true
    };
  }
}

/**
 * Main POST function - uses XHR for Safari, fetch for others
 */
export async function safePost(url, bodyObject, additionalHeaders = {}) {
  // Always use XHR for POST requests to avoid Safari body stream issues
  // XHR is more reliable across all browsers for this use case
  return xhrPost(url, bodyObject, additionalHeaders);
}

/**
 * Safe fetch for GET requests (less problematic)
 */
export async function safeFetch(url, options = {}) {
  try {
    const response = await fetch(url, {
      ...options,
      cache: 'no-store'
    });
    
    let data;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = { parseError: true };
    }
    
    return {
      ok: response.ok,
      status: response.status,
      data,
      parseError: null,
      headers: response.headers,
      networkError: false
    };
  } catch (e) {
    return {
      ok: false,
      status: 0,
      data: { error: 'Network error', detail: e.message },
      parseError: e,
      headers: null,
      networkError: true
    };
  }
}

/**
 * Wrapper for GET requests
 */
export async function safeGet(url, headers = {}) {
  return safeFetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      ...headers
    }
  });
}
